/* The physical card-set auction, as the portal and the homepage read it from
 * GET /auctions/current (everyone) and GET /auctions/current/me (a member). The
 * rules live on the server (lib/auction.js); these are the words and sums the two
 * pages share. Pure, so auction.test.ts can pin them. */

export type Bidder = { name: string; username: string | null };

export type AuctionTop = {
  rank: number;
  amountCents: number;
  bidder: Bidder;
  at: string;
};

export type AuctionLot = {
  id: number;
  lotNumber?: number;
  title: string;
  description: string;
  imageUrl: string | null;
  status: 'draft' | 'live' | 'closed' | 'settled' | 'cancelled';
  live: boolean;
  openingCents: number;
  minRaiseCents: number;
  extensionSeconds: number;
  depositInc: number;
  startsAt: string;
  endsAt: string;
  closedAt: string | null;
  settledAt: string | null;
};

export type AuctionOffer = {
  id: number;
  status: 'offered' | 'paid' | 'backed_out';
  rank: number;
  amountCents: number;
  payBy: string | null;
  paidAt: string | null;
  paidVia: 'stripe' | 'bank' | null;
  orderId: number | null;
};

export type AuctionYou = {
  highestBidCents: number | null;
  rank: number | null;
  hold: { status: 'held' | 'released' | 'forfeited'; inc: number } | null;
  balanceInc: number;
  availableInc: number;
  depositInc: number;
  canBid: boolean;
  reason: 'NOT_LIVE' | 'LEADING' | 'DEPOSIT' | null;
  offer: AuctionOffer | null;
};

export type AuctionView = {
  auction: AuctionLot | null;
  top?: AuctionTop[];
  bidders?: number;
  nextMinCents?: number;
  you?: AuctionYou | null;
};

/** "A$20,250" — whole dollars, the way a bid is spoken. */
export const aud = (cents: number): string =>
  `A$${Math.round(Number(cents || 0) / 100).toLocaleString('en-AU')}`;

/** "Barry M. · @baz0893" — the server already shortened the surname. */
export const bidderLabel = (b: Bidder | null | undefined): string => {
  if (!b) return 'Nobody yet';
  return b.username ? `${b.name} · @${b.username}` : b.name;
};

/** The whole-dollar amounts a bidder is offered as quick picks, from the next acceptable bid. */
export const quickBids = (
  nextMinCents: number,
  minRaiseCents: number
): number[] => {
  const base = Math.max(0, Math.round(nextMinCents));
  const step = Math.max(100, Math.round(minRaiseCents));
  return [base, base + step * 2, base + step * 4];
};

/** A typed amount in dollars → cents, or null when it is not a whole-dollar number. */
export const dollarsToCents = (raw: string | number): number | null => {
  if (/^\s*-/.test(String(raw ?? ''))) return null;
  const s = String(raw ?? '').replace(/[^\d.]/g, '');
  if (!s) return null;
  const n = Number(s);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n) * 100;
};

/** Words for the state line under the title. */
export const statusWords = (
  lot: AuctionLot,
  now: number = Date.now()
): string => {
  if (lot.status === 'cancelled') return 'This auction was cancelled.';
  if (lot.status === 'settled') return 'Sold. Thank you to everyone who bid.';
  if (lot.status === 'closed')
    return 'Bidding has closed. The winning bidder is completing the purchase.';
  if (lot.status === 'live' && new Date(lot.startsAt).getTime() > now)
    return 'Bidding opens soon.';
  if (lot.status === 'live' && new Date(lot.endsAt).getTime() <= now)
    return 'Bidding has closed.';
  return 'Bidding is open.';
};

/** Why a member cannot bid right now, in their words. */
export const cannotBidWords = (
  you: AuctionYou | null | undefined
): string | null => {
  if (!you || you.canBid) return null;
  if (you.reason === 'LEADING')
    return 'You hold the top bid. Sit tight — we will email you if someone outbids you.';
  if (you.reason === 'DEPOSIT') {
    return `Bidding needs ${you.depositInc.toLocaleString('en-AU')} INC free in your balance as a deposit; you have ${you.availableInc.toLocaleString('en-AU')} INC free.`;
  }
  if (you.reason === 'NOT_LIVE') return 'Bidding is not open right now.';
  return 'You cannot bid right now.';
};

/** "2d 03h 15m 09s" from a deadline. Never negative. */
export const leftWords = (
  endsAt: string | Date,
  now: number = Date.now()
): string => {
  const ms = Math.max(0, new Date(endsAt).getTime() - now);
  const s = Math.floor(ms / 1000);
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return d > 0
    ? `${d}d ${pad(h)}h ${pad(m)}m ${pad(sec)}s`
    : `${pad(h)}h ${pad(m)}m ${pad(sec)}s`;
};

/** The public API's base for a read that needs no sign-in. */
export const apiBase = (): string =>
  String(process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
